<template>
  <div>
    <div class="pa-3">
      <v-btn flat @click="$router.replace({ name: 'assetwise-broadcast-executive' })">
        <v-icon left>arrow_back</v-icon>
        {{ $t('Back') }}
      </v-btn>
    </div>

    <v-container>
      <h3 class="headline">{{ $t('Broadcast Detail') }}</h3>
    </v-container>

    <v-container>
      <div class="form-control" :class="{ 'has-error': errors.has('name') }">
        <div>{{ $t('Broadcast Name') }}</div>
        <input
          v-model="form.name"
          v-validate="'required'"
          type="text"
          name="name"
          :data-vv-as="$t('Broadcast Name')"
          :readonly="readonly"
        />
        <p v-if="errors.has('name')" class="text-error">{{ errors.first('name') }}</p>
      </div>
    </v-container>

    <v-divider />

    <v-container>
      <div class="form-control">
        <div>{{ $t('group_audience') }}</div>
        <v-radio-group v-model="form.target" row hide-details :readonly="readonly">
          <v-radio value="all" :label="$t('All')" color="primary" />
          <v-radio value="custom" :label="$t('custom')" color="primary" />
        </v-radio-group>
        <v-layout v-if="form.target === 'custom'" wrap class="mt-3">
          <v-flex xs12 sm6 class="pa-1">
            <div>{{ $t('asset_wise.team') }}</div>
            <TeamOptionListProvider>
              <template #default="{ options, loading }">
                <multiselect
                  v-model="form.teams"
                  :options="options.filter((option) => !!option.value)"
                  track-by="value"
                  label="text"
                  multiple
                  :show-labels="false"
                  :close-on-select="false"
                  :loading="loading"
                  :disabled="readonly"
                />
              </template>
            </TeamOptionListProvider>
          </v-flex>
          <v-flex xs12 sm6 class="pa-1">
            <div>{{ $t('name') }}</div>
            <UserOptionListProvider :teams="teamTexts" type="broker" text-type="broker">
              <template #default="{ options, loading }">
                <multiselect
                  v-model="form.users"
                  :options="options.filter((option) => !!option.value)"
                  track-by="value"
                  label="text"
                  multiple
                  :show-labels="false"
                  :close-on-select="false"
                  :loading="loading"
                  :disabled="readonly"
                />
              </template>
            </UserOptionListProvider>
          </v-flex>
        </v-layout>
      </div>
    </v-container>

    <v-divider />

    <v-container>
      <div class="form-control">
        <div>{{ $t('Message Type') }}</div>
        <v-layout wrap class="mt-3">
          <v-flex :xs12="$vuetify.breakpoint.xsOnly" shrink class="mr-3">
            <v-checkbox
              v-model="form.messageTypes"
              :label="$t('Text')"
              value="text"
              hide-details
              color="primary"
              :readonly="readonly"
              class="ma-0 pa-0"
              @change="updateMessages()"
            />
          </v-flex>
          <v-flex :xs12="$vuetify.breakpoint.xsOnly" shrink>
            <v-checkbox
              v-model="form.messageTypes"
              :label="$t('Image')"
              value="image"
              hide-details
              color="primary"
              :readonly="readonly"
              class="ma-0 pa-0"
              @change="updateMessages()"
            />
          </v-flex>
        </v-layout>
        <v-layout wrap class="mt-3">
          <v-flex xs12 sm6 offset-sm3>
            <template v-for="message in form.messages">
              <div v-if="message.type === 'image'" :key="message.type" class="pa-2">
                <UploadFile v-model="message.url" :disabled="readonly" />
              </div>
              <div v-if="message.type === 'text'" :key="message.type" class="form-control pa-2">
                <AutoresizeTextarea
                  v-model="message.text"
                  :placeholder="$t('Message')"
                  :readonly="readonly"
                />
              </div>
            </template>
          </v-flex>
        </v-layout>
      </div>
    </v-container>

    <v-divider />

    <v-container>
      <div class="form-control">
        <v-layout wrap>
          <v-flex shrink>{{ $t('Schedule Broadcast') }}</v-flex>
          <v-spacer />
          <v-flex shrink>
            <v-switch
              v-model="form.isScheduledBroadcastEnabled"
              color="primary"
              hide-details
              :readonly="readonly"
              class="ma-0 pa-0"
            />
          </v-flex>
        </v-layout>
        <v-layout v-if="form.isScheduledBroadcastEnabled" wrap>
          <v-flex xs12 sm8 class="pa-1">
            <v-menu
              ref="scheduledBroadcastDateMenu"
              lazy
              min-width="0"
              :close-on-content-click="false"
            >
              <template #activator="{ on }">
                <input
                  :value="form.scheduledBroadcastDatetime[0]"
                  type="text"
                  readonly
                  v-on="readonly ? undefined : on"
                />
              </template>
              <v-date-picker
                v-model="form.scheduledBroadcastDatetime[0]"
                @input="$refs.scheduledBroadcastDateMenu.save(form.scheduledBroadcastDatetime[0])"
              />
            </v-menu>
          </v-flex>
          <v-flex xs12 sm4 class="pa-1">
            <v-menu
              ref="scheduledBroadcastTimeMenu"
              lazy
              min-width="0"
              :close-on-content-click="false"
            >
              <template #activator="{ on }">
                <input
                  :value="form.scheduledBroadcastDatetime[1]"
                  type="text"
                  readonly
                  v-on="readonly ? undefined : on"
                />
              </template>
              <v-time-picker
                v-model="form.scheduledBroadcastDatetime[1]"
                :allowed-minutes="(m) => m % 10 === 0"
                @click:minute="
                  $refs.scheduledBroadcastTimeMenu.save(form.scheduledBroadcastDatetime[1])
                "
              />
            </v-menu>
          </v-flex>
        </v-layout>
      </div>
    </v-container>

    <v-container v-if="!readonly">
      <div :style="{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }">
        <v-btn
          outline
          color="grey"
          @click="$router.replace({ name: 'assetwise-broadcast-executive' })"
        >
          <v-icon left>arrow_back</v-icon>
          {{ $t('Back') }}
        </v-btn>
        <v-btn outline color="grey" @click="save('draft')">
          <v-icon left>save</v-icon>
          {{ $t('save draft') }}
        </v-btn>
        <v-btn depressed color="primary" @click="save('send')">
          {{ $t('Send') }}
          <v-icon right>send</v-icon>
        </v-btn>
      </div>
    </v-container>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import dayjs from 'dayjs'
import multiselect from 'vue-multiselect'
import UploadFile from '@/components/elements/uploadfile.vue'
import AutoresizeTextarea from '@/components/elements/AutoresizeTextarea.vue'
import TeamOptionListProvider from '../providers/TeamOptionListProvider.vue'
import UserOptionListProvider from '../providers/UserOptionListProvider.vue'
import { createBroadcast, editBroadcast, getTeams, getUsers } from '@/api/assetwise'

export default {
  components: {
    multiselect,
    TeamOptionListProvider,
    UserOptionListProvider,
    AutoresizeTextarea,
    UploadFile
  },
  beforeRouteEnter(to, from, next) {
    const { id, data } = to.params

    if (id && !data)
      return next({ name: 'assetwise-broadcast-executive', params: { ...to.params } })

    next(async (vm) => {
      if (!id) return

      const teams = await getTeams({
        page_id: vm.selectedBot.pageid,
        limit: Number.MAX_SAFE_INTEGER,
        offset: 0
      }).then((response) => response.data.data)

      const users = await getUsers({
        page_id: vm.selectedBot.pageid,
        limit: Number.MAX_SAFE_INTEGER,
        offset: 0
      }).then((response) => response.data.data)

      vm.form = JSON.parse(
        JSON.stringify({
          name: data.name,
          target: data.target,
          teams: (data.team_ids || []).map((id) => {
            const team = teams.find((item) => +item.team_id === +id)
            if (!team) {
              return {
                value: `${id}`,
                text: `${id}`
              }
            }
            return {
              value: team.team_id,
              text: team.team_name
            }
          }),
          users: (data.user_ids || []).map((id) => {
            const user = users.find((item) => +item.user_id === +id)
            if (!user) {
              return {
                value: `${id}`,
                text: `${id}`
              }
            }
            return {
              value: user.user_id,
              text: user.broker_name || user.social_name
            }
          }),
          messageTypes: data.message_type,
          messages: ['image', 'text']
            .map((type) => {
              const message = data.messages.find((message) => message.type === type)
              if (!message) return null
              if (message.type === 'image') {
                return {
                  type: 'image',
                  url: message.image_url
                }
              }
              return message
            })
            .filter((message) => !!message),
          isScheduledBroadcastEnabled: !!data.schedule_timestamp,
          scheduledBroadcastDatetime: data.schedule_timestamp
            ? [
                dayjs(data.schedule_timestamp).format('YYYY-MM-DD'),
                dayjs(data.schedule_timestamp).format('HH:mm')
              ]
            : vm.form.scheduledBroadcastDatetime
        })
      )
    })
  },
  props: {
    edit: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        name: `Broadcast_${dayjs().format('YYYY_MM_DD_HH_mm')}`,
        target: 'all',
        teams: [],
        users: [],
        messageTypes: [],
        messages: [],
        isScheduledBroadcastEnabled: false,
        scheduledBroadcastDatetime: [
          dayjs().format('YYYY-MM-DD'),
          dayjs().add(1, 'hours').startOf('hours').format('HH:mm')
        ]
      },
      isScheduledBroadcastDateMenuVisible: false,
      isScheduledBroadcastTimeMenuVisible: false
    }
  },
  computed: {
    ...mapGetters(['selectedBot']),
    teamTexts() {
      return this.form.teams.map((team) => team.text)
    }
  },
  methods: {
    updateMessages() {
      const types = ['image', 'text']

      this.form.messages = types
        .map((type) => {
          const hasType = this.form.messageTypes.includes(type)

          if (hasType) {
            const message = this.form.messages.find((message) => message.type === type)

            if (!message) {
              return {
                image: {
                  type: 'image',
                  url: ''
                },
                text: {
                  type: 'text',
                  text: ''
                }
              }[type]
            }

            return message
          } else {
            return null
          }
        })
        .filter((message) => !!message)
    },
    async save(type) {
      const isValid = await this.$validator.validate()

      if (!isValid) return

      try {
        const payload = {
          page_id: this.selectedBot.pageid,
          name: this.form.name,
          target: this.form.target,
          message_type: this.form.messageTypes,
          messages: this.form.messages.map((message) => {
            if (message.type === 'image') {
              return {
                type: 'image',
                image_url: message.url
              }
            }
            return message
          }),
          ...(this.form.target === 'custom' && {
            user_ids: this.form.users.map((user) => user.value),
            team_ids: this.form.teams.map((team) => team.value)
          }),
          schedule_timestamp: this.form.isScheduledBroadcastEnabled
            ? this.form.scheduledBroadcastDatetime.join(' ')
            : null,
          status: {
            draft: 'draft',
            send: 'sent'
          }[type]
        }

        if (this.edit) {
          payload.id = this.$route.params.id
          payload.update_by = this.$store.state.userid
          await editBroadcast(payload)
        } else {
          payload.create_by = this.$store.state.userid
          await createBroadcast(payload)
        }

        this.$router.replace({ name: 'assetwise-broadcast-executive' })
      } catch (error) {
        this.$swal(this.$t('Something went wrong'), undefined, 'error')
        console.error(error)
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/view/template/AssetWise/ExecutiveBroadcastCreate/index.vue